import React, { useEffect, useRef, useState } from "react";
import styles from "./SitePrestationEditor.module.scss";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import { DatePicker } from "../../common/DatePicker";
import Editor from "../../common/Editor";
import { MODE_CREATE, MODE_EDIT, PRESTATION_HOURS_REGEX } from "../../codes";
import moment from "moment";
import { useMutation, useQuery } from "@tanstack/react-query";
import { apiGetContext } from "../../api/ApiApp";
import useToast from "../../util/useToast";
import { DeleteModal } from "../../common/AppModal";
import { PrestationErrors } from "../../errors/ErrorsMapping";
import { apiCreateSitePrestation, apiDeleteSitePrestation, apiUpdateSitePrestation } from "../../api/ApiSites";
import { formatDuration } from "../../util/util";
import { TextEditor } from "../../lexical/TextEditor";
import clsx from "clsx";

const SitePrestationEditor = ({ show, mode, hide, onSubmit, site, prestation = {}, onDelete }) => {
  const { showToastSuccess } = useToast();

  const [deleteModal, setDeleteModal] = useState(false);

  const contextQuery = useQuery(apiGetContext());
  const { workers } = contextQuery.data;

  const descriptionEditorRef = useRef(null);
  const progressReportEditorRef = useRef(null);

  const [formErrors, setFormErrors] = useState({});

  const [formData, setFormData] = useState({
    workers: {},
    duration: "",
    description: "",
    progressReport: "",
    date: moment().format("YYYY-MM-DD")
  });

  const createMutation = useMutation(apiCreateSitePrestation(site.id));
  const editMutation = useMutation(apiUpdateSitePrestation(site.id, prestation.id));
  const deleteMutation = useMutation(apiDeleteSitePrestation(site.id, prestation.id));

  useEffect(() => {
    if (show) {
      setFormErrors({});

      const workers = {};

      (prestation.workers || []).forEach(worker => workers[worker.workerId] = {...worker});

      setFormData({
        workers,
        duration: prestation.duration || "",
        description: prestation.description || "",
        progressReport: prestation.progressReport || "",
        date: prestation.date || moment().format("YYYY-MM-DD"),
      });

      // descriptionRef.current.focus();
    }
  }, [show, prestation, site.id]);

  const submit = () => {
    let promise;

    const data = { ...formData };
    data.description = descriptionEditorRef.current?.getHtml() || "";
    data.progressReport = progressReportEditorRef.current?.getHtml() || "";
    data.workers = Object.keys(data.workers).map(workerId => ({
      workerId,
      ...data.workers[workerId]
    }));

    if (mode === MODE_EDIT) {
      promise = editMutation.mutateAsync(data);
    } else {
      promise = createMutation.mutateAsync(data);
    }

    return promise
      .then(() => {
        return (onSubmit ? onSubmit() : Promise.resolve()).then(() => {
          if (mode === MODE_EDIT) {
            showToastSuccess("Prestation modifiée");
          } else {
            showToastSuccess("Prestation ajoutée");
          }
        });
      })
      .then(hide)
      .catch((err) => {
        if (err.response?.status === 400) {
          setFormErrors(err.response.data.errors);
        }
      });
  };

  const updateFormData = (field, additionalData) => e => {
    const values = {};

    switch (field) {
      case "worker":
        if (PRESTATION_HOURS_REGEX.test(e.target.value)) {
          values.workers = formData.workers;
          values.workers[additionalData] = values.workers[additionalData] || {};
          values.workers[additionalData].hours = e.target.value.replace(",", ".");
        }
        break;
      case "worker_alert":
        values.workers = formData.workers;
        values.workers[additionalData] = values.workers[additionalData] || {};
        values.workers[additionalData].alert = !values.workers[additionalData].alert;
        break;
      case "date":
        values[field] = e;
        break;
      case "duration":
        if (/^(\d{1,2}[.,]?\d?\d?)?$/.test(e.target.value)) {
          values[field] = e.target.value.replace(",", ".");
        }

        break;
      default:
        values[field] = e.target.value;
        break;
    }

    setFormData(prev => ({ ...prev, ...values }));
  };

  return <Editor
    show={show}
    title={mode === MODE_CREATE ? "Ajouter une prestation" : "Modifier une prestation"}
    onSubmit={submit}
    onCancel={hide}
    onDelete={() => setDeleteModal(true)}
    submitLabel="Sauvegarder"
    cancelLabel="Annuler"
  >
    <Form.Group className="mb-2" controlId="site-prestation-editor-description">
      <Row>
        <Form.Label column sm={3}>Description</Form.Label>
        <Col sm={9}>
          <TextEditor ref={descriptionEditorRef} value={formData.description} />

          <Form.Control.Feedback type="invalid">
            {PrestationErrors.description[formErrors.description]}
          </Form.Control.Feedback>
        </Col>
      </Row>
    </Form.Group>

    <Form.Group className="mb-2" controlId="site-prestation-editor-progress-report">
      <Row>
        <Form.Label column sm={3}>Relevé</Form.Label>
        <Col sm={9}>
          <TextEditor ref={progressReportEditorRef} value={formData.progressReport} />

          <Form.Control.Feedback
            type="invalid">{PrestationErrors.progressReport[formErrors.progressReport]}</Form.Control.Feedback>
        </Col>
      </Row>
    </Form.Group>

    <Form.Group className="mb-2" controlId="site-prestation-editor-date">
      <Row>
        <Form.Label column sm={3}>Date</Form.Label>
        <Col sm={9}>
          <DatePicker
            value={formData.date}
            onChange={updateFormData("date")}
            isInvalid={!!formErrors.date}
            error={PrestationErrors.date[formErrors.date]}
          />
        </Col>
      </Row>
    </Form.Group>

    <Row>
      <Form.Label column sm={3}>Heures</Form.Label>

      <Col sm={9}>
        <div className={styles.workers}>
          {workers.sort((a, b) => a.name.localeCompare(b.name)).map(w => {
            const worker = formData.workers[w.id];

            return <div key={w.id} className={styles.worker}>
              <div>
                <input
                  type="text"
                  value={worker?.hours || ""}
                  onChange={updateFormData("worker", w.id)}
                  maxLength="255"
                />
              </div>
              <div className={styles.workerHours}>{formatDuration(worker?.hours)}</div>
              <div className={clsx(styles.workerAlert, worker?.alert ? styles.alert : null)}>
                <span className="material-icons" onClick={updateFormData("worker_alert", w.id)}>error</span>
              </div>
              <div className={styles.workerName}>{w.name}</div>
            </div>;
          })}
        </div>
      </Col>
    </Row>

    <DeleteModal
      show={deleteModal}
      title="Supprimer la prestation"
      onCancel={() => setDeleteModal(false)}
      onSubmit={() => {
        return deleteMutation.mutateAsync(null)
          .then(onDelete)
          .then(() => {
            showToastSuccess("Prestation supprimée");

            hide();
            setDeleteModal(false);
          });
      }}
      cancelLabel="Annuler"
      submitLabel="Supprimer"
    >
      Etes-vous sûr de vouloir supprimer cette prestation ?
    </DeleteModal>
  </Editor>;
};

export default SitePrestationEditor;